/**
 * A chunk that is no longer there, and the one reload that fixes it.
 *
 * Extracted from `routes/RouteError.tsx` so the SIGNER can have it too. The console and the signing
 * ceremony are two separate entry points — `index.html` and `sign.html` — and only the console had a
 * boundary. A signer who submitted a change request while Pages was mid-deploy got a completely
 * blank page: `sign.tsx` lazy-loads `HoldPanel`, the import rejected with "Failed to fetch
 * dynamically imported module", nothing caught it, and React unmounted the whole tree. The request
 * had been saved; the page just stopped existing. Measured on production, 2026-09-15.
 *
 * Keeping the two copies in step by hand was never going to work, and importing `RouteError` into the
 * ceremony would drag react-router-dom into a bundle that has no router. So both import this.
 */

/**
 * Whether something failed because its CODE could not be fetched, rather than because it threw.
 *
 * Matched on the message because that is all there is: the three engines word it differently and
 * none of them exposes a code. Chrome throws a `TypeError` reading "Failed to fetch dynamically
 * imported module", Firefox "error loading dynamically imported module", Safari "Importing a module
 * script failed"; Vite's own CSS preloader throws "Unable to preload CSS", and bundlers that keep
 * webpack's vocabulary name the error `ChunkLoadError`. A false positive costs one reload; a false
 * negative is the status quo.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { name, message } = error as { readonly name?: unknown; readonly message?: unknown }
  if (name === 'ChunkLoadError') return true
  if (typeof message !== 'string') return false
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /unable to preload css/i.test(message)
  )
}

const RELOAD_STAMP_KEY = 'flowza.stale-chunk-reload'

/**
 * Long enough that a reload which did not help cannot loop, short enough that a SECOND deploy later
 * in the same tab still recovers by itself rather than being told to press a button.
 */
const RELOAD_COOLDOWN_MS = 30_000

/**
 * Take the one reload this tab is allowed right now, or refuse it.
 *
 * One key for both entry points on purpose. A reader who is bounced from the console into the
 * ceremony, or back, is the same person in the same tab, and two independent budgets would let a
 * genuinely broken deploy reload them twice.
 */
export function claimReload(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY) ?? '0')
    if (Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) return false
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(now))
    return true
  } catch {
    // A private window, or blocked site data. Without somewhere to record the attempt there is no
    // way to stop a loop, so this browser is told what happened instead of being reloaded.
    return false
  }
}
